'use client';

import { useRef } from 'react';
import { gsap, useGSAP } from '@/components/motion/gsap';
import Eyebrow from '@/components/ui/Eyebrow';
import {
  AiScene,
  COMPACT_H,
  COMPACT_W,
  EdgingScene,
  FitScene,
  LabScene,
  ModuleRail,
  PmsScene,
  RAIL_PITCH,
  SCENES,
  STAGE_H,
  STAGE_W,
  ShipScene,
  StoreScene,
  TokenFaces,
  WindowChrome,
} from './OrderTourScenes';

/**
 * The platform, told as one order: a frame is bought in the store, AI reads
 * the prescription, a camera tries the frame on and measures the face, the
 * order joins the patient record, it is verified and
 * routed to a lab, a barcode loads it on the edger, and it is delivered. It is
 * the same order #4821 the hero drops into the platform and the closing block
 * marks as delivered, so the page has one story.
 *
 * One element travels the whole way — `[data-token]` — laid over a slot in
 * each chapter and changing face as it goes, so each module is seen picking the
 * order up from the last rather than being cut to.
 */
const CHAPTERS = [
  {
    eyebrow: 'E-Commerce · Remote Order Entry',
    title: 'An order comes in.',
    body: 'A customer picks a frame in your online store, or an ECP keys it into Remote Order Entry. Either way it is one order in LenSync the moment checkout completes.',
  },
  {
    eyebrow: 'LenSync AI',
    title: 'AI reads the prescription.',
    body: 'The prescription is recognized, validated and checked for errors automatically — nobody retypes a number.',
  },
  {
    eyebrow: 'Camera fitting',
    title: 'The camera takes the measurements.',
    body: 'On a laptop or phone camera the customer tries the frame on, and LenSync measures pupillary distance and segment height on the spot.',
  },
  {
    eyebrow: 'Practice Management · EMR',
    title: 'It joins the patient record.',
    body: 'The order sits with the patient and their prescription, so the whole team works from the same record at the same time.',
  },
  {
    eyebrow: 'Lab Management System',
    title: 'The right lab gets a complete job.',
    body: 'Lens and coating availability, thickness and bevel placement are verified before the order leaves — then it is routed to the lab that makes it.',
  },
  {
    eyebrow: 'Edging Module',
    title: 'Entered once. Edged right.',
    body: 'Work is entered once, from ordering to edging. When the lenses arrive, a barcode loads the job on the edger and guarantees the result.',
  },
  {
    eyebrow: 'Status Tracking',
    title: 'Delivered, and tracked all the way.',
    body: 'Real-time updates from production to the customer’s door, in the same place the order started.',
  },
];

const OrderTour = (props) => {
  const root = useRef(null);

  useGSAP(
    () => {
      const section = root.current;
      if (!section) return;
      const q = (s) => gsap.utils.toArray(s, section);
      const one = (s) => q(s)[0];
      const stage = one('[data-stage]');
      const fit = one('[data-stage-fit]');

      // ── scale the fixed design space into whatever room it has ────────
      // Below lg the stage switches to the portrait layout; see OrderTourScenes.
      const narrowQuery = window.matchMedia('(max-width: 1023px)');
      const scale = () => {
        const compact = narrowQuery.matches;
        const W = compact ? COMPACT_W : STAGE_W;
        const H = compact ? COMPACT_H : STAGE_H;
        stage.dataset.compact = String(compact);
        stage.style.width = `${W}px`;
        stage.style.height = `${H}px`;
        const s = Math.min(fit.clientWidth / W, fit.clientHeight / H);
        stage.style.transform = `scale(${s})`;
        stage.style.left = `${(fit.clientWidth - W * s) / 2}px`;
        // Side by side the stage centres on the text; stacked, it sits
        // directly under it so the two read as one block.
        stage.style.top = compact ? '0px' : `${(fit.clientHeight - H * s) / 2}px`;
      };
      scale();
      const ro = new ResizeObserver(scale);
      ro.observe(fit);

      const mm = gsap.matchMedia();
      mm.add(
        { motion: '(prefers-reduced-motion: no-preference)', compact: '(max-width: 1023px)' },
        (ctx) => {
          if (!ctx.conditions.motion) return;
          // The two layouts put every slot somewhere else, so crossing the
          // breakpoint rebuilds the whole timeline over the new one.
          scale();
          const token = one('[data-token]');
          const face = (k) => one(`[data-face="${k}"]`);

          // Slot geometry in stage units. offsetLeft/Top are layout values, so
          // the CSS scale on the stage does not enter into them.
          const box = (name) => {
            const el = one(`[data-anchor="${name}"]`);
            let x = 0;
            let y = 0;
            let n = el;
            while (n && n !== stage) {
              x += n.offsetLeft;
              y += n.offsetTop;
              n = n.offsetParent;
            }
            return { x, y, w: el.offsetWidth, h: el.offsetHeight };
          };

          const tl = gsap.timeline({
            defaults: { ease: 'power3.inOut', duration: 1 },
            scrollTrigger: {
              trigger: section,
              start: 'top top',
              // The frame's height (100svh), not window.innerHeight: on a phone
              // innerHeight changes as the toolbar hides, and a pin whose length
              // changes between refreshes shifts everything below it.
              end: () => `+=${Math.round(one('[data-frame-h]').offsetHeight * 7)}`,
              pin: true,
              scrub: 0.9,
              invalidateOnRefresh: true,
              anticipatePin: 1,
            },
          });

          // Move the token onto a slot and change its face on the way.
          const morph = (to, from, at, radius = 12, toFace = to, dur = 1.3) => {
            tl.to(
              token,
              {
                x: () => box(to).x,
                y: () => box(to).y,
                width: () => box(to).w,
                height: () => box(to).h,
                borderRadius: radius,
                duration: dur,
              },
              at
            )
              .to(
                face(from),
                { autoAlpha: 0, duration: dur * 0.35, ease: 'power1.in' },
                at + dur * 0.3
              )
              .to(
                face(toFace),
                { autoAlpha: 1, duration: dur * 0.4, ease: 'power1.out' },
                at + dur * 0.45
              );
          };

          // Chapter change: text, progress, rail, breadcrumb, scene.
          const chapter = (i, at) => {
            tl.to(
              q(`[data-chapter="${i - 1}"]`),
              { autoAlpha: 0, y: -24, duration: 0.5, ease: 'power2.in' },
              at
            )
              .fromTo(
                q(`[data-chapter="${i}"]`),
                { autoAlpha: 0, y: 28 },
                { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', immediateRender: false },
                at + 0.45
              )
              .to(one(`[data-progress="${i - 1}"]`), { scaleX: 1, duration: 0.3 }, at)
              .to(
                one('[data-counter]'),
                { yPercent: (-100 / CHAPTERS.length) * i, duration: 0.7, ease: 'power3.inOut' },
                at + 0.1
              )
              .to(one('[data-nav-pill]'), { y: i * RAIL_PITCH, duration: 0.6 }, at + 0.1)
              .to(one(`[data-nav-on="${i - 1}"]`), { autoAlpha: 0, duration: 0.2 }, at + 0.1)
              .to(one(`[data-nav-on="${i}"]`), { autoAlpha: 1, duration: 0.25 }, at + 0.45)
              .to(one(`[data-crumb="${i - 1}"]`), { autoAlpha: 0, duration: 0.3 }, at + 0.1)
              .to(one(`[data-crumb="${i}"]`), { autoAlpha: 1, duration: 0.3 }, at + 0.4)
              .to(
                one(`[data-scene="${SCENES[i - 1].key}"]`),
                { autoAlpha: 0, scale: 0.97, duration: 0.6, ease: 'power2.in' },
                at
              )
              .fromTo(
                one(`[data-scene="${SCENES[i].key}"]`),
                { autoAlpha: 0, scale: 1.03 },
                {
                  autoAlpha: 1,
                  scale: 1,
                  duration: 0.8,
                  ease: 'power3.out',
                  immediateRender: false,
                },
                at + 0.5
              );
          };

          // ── initial frame ─────────────────────────────────────────────
          // The markup is the finished state, so everything the scroll is
          // going to reveal is hidden here first.
          const frame = box('frame');
          gsap.set(token, {
            x: frame.x,
            y: frame.y,
            width: frame.w,
            height: frame.h,
            borderRadius: 12,
            autoAlpha: 1,
          });
          gsap.set(q('[data-chapter]:not([data-chapter="0"])'), { autoAlpha: 0 });
          gsap.set(q('[data-progress]'), { scaleX: 0, transformOrigin: '0% 50%' });
          gsap.set(q('[data-queue-row]'), { y: -60 });
          gsap.set(one('[data-count="from"]'), { autoAlpha: 1 });
          gsap.set(
            q(
              '[data-count="to"], [data-added], [data-cursor], [data-cart-note], [data-field], [data-bbox], [data-validated], [data-mesh], [data-pupil], [data-pd], [data-glasses], [data-seg], [data-fit-cell], [data-fit-note], [data-fit-done], [data-old-row], [data-viewer], [data-viewers-label], [data-rx-link], [data-check], [data-xsec-dim], [data-lab], [data-lab-ring], [data-routed], [data-station-on], [data-scan-note], [data-measure], [data-wheel], [data-lens-label], [data-route], [data-track], [data-parcel-done]'
            ),
            { autoAlpha: 0 }
          );
          gsap.set(one('[data-station-fill]'), { scaleX: 0, transformOrigin: '0% 50%' });
          // pathLength is 1000 on these: GSAP rounds px values, so a 0–1 range would snap
          gsap.set(q('[data-lens-path], [data-flight]'), { strokeDashoffset: 1000 });
          gsap.set(one('[data-counter]'), { yPercent: 0 });

          // The structured values are decoded on screen; remember what they
          // say, blank them, and put them back if this layout is torn down.
          const values = q('[data-value]');
          const finals = values.map((el) => el.textContent);
          values.forEach((el) => (el.textContent = '·····'));
          const fitCells = q('[data-fit-cell]');
          const fitValues = q('[data-fit-value]');
          const fitFinals = fitValues.map((el) => el.textContent);
          fitValues.forEach((el) => (el.textContent = '··.·'));

          // ── 01 · store: the frame is bought and becomes an order ──────
          tl.fromTo(
            one('[data-cursor]'),
            { x: 22, y: 22 },
            { x: 0, y: 0, autoAlpha: 1, duration: 0.6, ease: 'power3.out' },
            0.2
          )
            .to(
              one('[data-cursor]'),
              { scale: 0.78, duration: 0.12, yoyo: true, repeat: 1, ease: 'power2.out' },
              0.85
            )
            .fromTo(
              one('[data-added]'),
              { scale: 0.4 },
              { scale: 1, autoAlpha: 1, duration: 0.35, ease: 'back.out(3)' },
              1
            )
            .to(token, { scale: 1.05, duration: 0.3, ease: 'back.out(2)' }, 1)
            .to(one('[data-cursor]'), { autoAlpha: 0, duration: 0.2 }, 1.5)
            .to(token, { scale: 1, duration: 0.4 }, 1.9)
            .to(q('[data-queue-row]'), { y: 0, duration: 0.8, stagger: 0.06 }, 1.9);
          morph('order', 'frame', 1.9);
          tl.fromTo(
            one('[data-cart-note]'),
            { y: 10 },
            { y: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out' },
            2.9
          )
            .to(one('[data-count="from"]'), { autoAlpha: 0, y: -6, duration: 0.2 }, 3)
            .fromTo(one('[data-count="to"]'), { y: 6 }, { y: 0, autoAlpha: 1, duration: 0.2 }, 3);

          // ── 02 · ai: the order opens into its prescription ────────────
          chapter(1, 4.4);
          morph('rx', 'order', 4.7, 16);
          tl.to(one('[data-scan]'), { top: '100%', duration: 1.3, ease: 'none' }, 5.9)
            .fromTo(
              q('[data-bbox]'),
              { scale: 1.5 },
              { scale: 1, autoAlpha: 1, duration: 0.25, stagger: 0.09, ease: 'power3.out' },
              6.1
            )
            .fromTo(
              q('[data-field]'),
              { x: 14 },
              { x: 0, autoAlpha: 1, duration: 0.4, stagger: 0.35, ease: 'power3.out' },
              6.5
            );
          values.forEach((el, i) => {
            tl.to(
              el,
              {
                duration: 0.5,
                ease: 'none',
                scrambleText: { text: finals[i], chars: '0123456789−+.', speed: 0.5 },
              },
              6.6 + i * 0.12
            );
          });
          tl.fromTo(
            one('[data-validated]'),
            { x: -10 },
            { x: 0, autoAlpha: 1, duration: 0.4, ease: 'power3.out' },
            8.3
          );

          // ── 03 · fit: the camera tries the frame on and measures ──────
          chapter(2, 9.4);
          morph('fit', 'rx', 9.7);
          tl.to(one('[data-cam-scan]'), { top: '100%', duration: 1.4, ease: 'none' }, 10.3)
            .fromTo(
              q('[data-mesh]'),
              { scale: 0, transformOrigin: '50% 50%' },
              { scale: 1, autoAlpha: 1, duration: 0.3, stagger: 0.02, ease: 'power2.out' },
              10.4
            )
            .fromTo(
              q('[data-pupil]'),
              { scale: 2.2, transformOrigin: '50% 50%' },
              { scale: 1, autoAlpha: 1, duration: 0.35, stagger: 0.12, ease: 'power3.out' },
              11.2
            )
            .fromTo(
              one('[data-pd]'),
              { y: 8 },
              { y: 0, autoAlpha: 1, duration: 0.4, ease: 'power3.out' },
              11.6
            )
            .fromTo(
              one('[data-glasses]'),
              { y: -46 },
              { y: 0, autoAlpha: 1, duration: 0.7, ease: 'back.out(1.4)' },
              12.2
            )
            .to(one('[data-fit-done]'), { autoAlpha: 1, duration: 0.25 }, 12.8)
            .to(q('[data-mesh]'), { autoAlpha: 0.35, duration: 0.4 }, 12.4)
            .fromTo(
              one('[data-seg]'),
              { x: -8 },
              { x: 0, autoAlpha: 1, duration: 0.4, ease: 'power3.out' },
              13
            )
            .fromTo(
              one('[data-fit-note]'),
              { x: -10 },
              { x: 0, autoAlpha: 1, duration: 0.4, ease: 'power3.out' },
              13.7
            );
          fitCells.forEach((cell, i) => {
            const at = i ? 13.1 + (i - 1) * 0.15 : 11.7;
            tl.to(cell, { autoAlpha: 1, duration: 0.25 }, at).to(
              fitValues[i],
              {
                duration: 0.5,
                ease: 'none',
                scrambleText: { text: fitFinals[i], chars: '0123456789.', speed: 0.5 },
              },
              at
            );
          });

          // ── 04 · pms: the prescription files itself on the patient ────
          chapter(3, 14.4);
          morph('record', 'fit', 14.7);
          tl.fromTo(
            q('[data-old-row]'),
            { y: 12 },
            { y: 0, autoAlpha: 0.7, duration: 0.5, stagger: 0.1, ease: 'power3.out' },
            15.3
          )
            .fromTo(
              q('[data-viewer]'),
              { scale: 0.3 },
              { scale: 1, autoAlpha: 1, duration: 0.35, stagger: 0.25, ease: 'back.out(3)' },
              16.2
            )
            .to(q('[data-viewers-label], [data-rx-link]'), { autoAlpha: 1, duration: 0.3 }, 16.9)
            .to(one('[data-rec-status="new"]'), { autoAlpha: 0, y: -6, duration: 0.2 }, 17.3)
            .fromTo(
              one('[data-rec-status="done"]'),
              { y: 6 },
              { y: 0, autoAlpha: 1, duration: 0.25 },
              17.35
            );

          // ── 05 · lab: verified complete, then sent to its lab ─────────
          chapter(4, 18.6);
          morph('job', 'record', 18.9);
          tl.fromTo(
            q('[data-lab]'),
            { y: 14 },
            { y: 0, autoAlpha: 1, duration: 0.5, stagger: 0.1, ease: 'power3.out' },
            19.2
          )
            .fromTo(
              q('[data-check]'),
              { x: -12 },
              { x: 0, autoAlpha: 1, duration: 0.35, stagger: 0.35, ease: 'power3.out' },
              20
            )
            .to(q('[data-xsec-dim]'), { autoAlpha: 1, duration: 0.4, stagger: 0.15 }, 20.5)
            // every lab gets its own order — then ours goes to the one that makes it
            .fromTo(
              q('[data-routed]'),
              { x: -36 },
              { x: 0, autoAlpha: 1, duration: 0.45, stagger: 0.22, ease: 'power3.out' },
              20.9
            )
            .to(one('[data-lab-ring]'), { autoAlpha: 1, duration: 0.3 }, 21.6)
            .to(token, { rotation: 2, scale: 1.04, duration: 0.3, ease: 'back.out(2)' }, 21.6)
            .to(
              token,
              {
                x: () => box('labslot').x,
                y: () => box('labslot').y,
                width: () => box('labslot').w,
                height: () => box('labslot').h,
                borderRadius: 8,
                duration: 1,
              },
              21.8
            )
            .to(token, { rotation: 0, scale: 1, duration: 0.35, ease: 'back.out(3)' }, 22.7);

          // ── 06 · edging: the job becomes a tray on the line ───────────
          chapter(5, 23.6);
          morph('tray', 'job', 23.9, 16);
          tl.to(one('[data-station-fill]'), { scaleX: 1, duration: 3, ease: 'none' }, 24.6)
            .to(q('[data-station-on]'), { autoAlpha: 1, duration: 0.25, stagger: 1 }, 24.6)
            .to(
              one('[data-beam]'),
              { left: '100%', duration: 0.7, ease: 'power1.inOut', yoyo: true, repeat: 1 },
              25.3
            )
            .fromTo(
              one('[data-scan-note]'),
              { y: 8 },
              { y: 0, autoAlpha: 1, duration: 0.4, ease: 'power3.out' },
              26.6
            )
            .to(one('[data-wheel]'), { autoAlpha: 1, duration: 0.2 }, 26)
            .to(one('[data-lens-path]'), { strokeDashoffset: 0, duration: 1.6, ease: 'none' }, 26.2)
            .fromTo(
              one('[data-wheel]'),
              { rotation: 0, svgOrigin: '0 0' },
              { rotation: 360, svgOrigin: '0 0', duration: 1.6, ease: 'none' },
              26.2
            )
            .to(one('[data-wheel]'), { autoAlpha: 0, duration: 0.2 }, 27.8)
            .to(q('[data-measure]'), { autoAlpha: 1, duration: 0.3, stagger: 0.12 }, 27.2)
            .fromTo(
              one('[data-lens-label]'),
              { scale: 0.7 },
              { scale: 1, autoAlpha: 1, duration: 0.35, ease: 'back.out(2.5)' },
              27.8
            );

          // ── 07 · ship: the tray becomes a parcel and flies out ────────
          chapter(6, 29);
          morph('hub', 'tray', 29.3, 10, 'parcel');
          const lift = () => Math.min(box('hub').y, box('dest').y) - 26;
          const tracks = q('[data-track]');
          tl.to(q('[data-route]'), { autoAlpha: 1, duration: 0.4, stagger: 0.12 }, 29.8)
            .to(tracks[0], { autoAlpha: 1, duration: 0.3 }, 30.4)
            .to(token, { x: () => box('dest').x, duration: 1.8, ease: 'power1.inOut' }, 31)
            .to(
              one('[data-flight]'),
              { strokeDashoffset: 0, duration: 1.8, ease: 'power1.inOut' },
              31
            )
            .to(token, { y: lift, duration: 0.9, ease: 'power1.out' }, 31)
            .to(token, { y: () => box('dest').y, duration: 0.9, ease: 'power1.in' }, 31.9)
            .to(tracks[1], { autoAlpha: 1, duration: 0.3 }, 31.4)
            .to(tracks[2], { autoAlpha: 1, duration: 0.3 }, 32.8)
            .fromTo(
              one('[data-parcel-done]'),
              { yPercent: 100 },
              { yPercent: 0, autoAlpha: 1, duration: 0.35, ease: 'power3.out' },
              32.8
            )
            .to(
              token,
              { scale: 1.12, duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out' },
              32.85
            )
            .to(one(`[data-progress="${CHAPTERS.length - 1}"]`), { scaleX: 1, duration: 0.6 }, 33)
            .to({}, { duration: 1.2 });

          return () => {
            values.forEach((el, i) => (el.textContent = finals[i]));
            fitValues.forEach((el, i) => (el.textContent = fitFinals[i]));
          };
        }
      );

      return () => {
        ro.disconnect();
        mm.revert();
      };
    },
    { scope: root }
  );

  return (
    <section
      ref={root}
      id={props.id}
      aria-label="How LenSync works, from order to delivery"
      className="relative min-h-[100lvh] overflow-hidden bg-ink text-white motion-reduce:min-h-0"
    >
      {/* ground: a bench grid and one blue light behind the window */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(90%_70%_at_60%_50%,#000,transparent)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-[62%] top-1/2 size-[70vmax] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-blue/20 blur-[140px]"
      />

      <div
        data-frame-h
        className="relative mx-auto flex h-[100svh] min-h-[600px] max-w-[1560px] flex-col gap-4 px-5 pb-5 pt-24 lg:flex-row lg:items-center lg:gap-14 lg:px-10 motion-reduce:h-auto motion-reduce:min-h-0 motion-reduce:py-20"
      >
        {/* the words */}
        <div className="relative shrink-0 lg:w-[33%]">
          <div className="hidden items-center justify-between sm:flex">
            <Eyebrow>One order, start to finish</Eyebrow>
            {/* 01 … 06, rolled like a counter */}
            <span
              aria-hidden
              className="flex items-baseline gap-1.5 font-mono text-xs text-white/40 motion-reduce:hidden"
            >
              <span className="block h-[1.4em] overflow-hidden text-white">
                <span data-counter className="block">
                  {CHAPTERS.map((c, i) => (
                    <span key={c.title} className="block h-[1.4em] leading-[1.4em]">
                      0{i + 1}
                    </span>
                  ))}
                </span>
              </span>
              / 0{CHAPTERS.length}
            </span>
          </div>
          {/* Every chapter sits in the same grid cell, so the block is as tall
              as the longest one and nothing below it has to guess a height. */}
          <div className="grid sm:mt-7 lg:min-h-[330px]">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.title}
                data-chapter={i}
                className="[grid-area:1/1] motion-reduce:mb-10 motion-reduce:[grid-area:auto]"
              >
                <p className="label text-primary-blue">{c.eyebrow}</p>
                <h2 className="display mt-3 text-balance text-[clamp(28px,3.4vw,56px)] text-white sm:mt-4">
                  {c.title}
                </h2>
                <p className="mt-3 max-w-[42ch] text-[14px] leading-[1.5] text-white/60 sm:mt-5 sm:text-[17px] sm:leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-6 hidden gap-1.5 lg:flex motion-reduce:hidden" aria-hidden>
            {CHAPTERS.map((c, i) => (
              <span
                key={c.title}
                className="h-[2px] flex-1 overflow-hidden rounded-full bg-white/15"
              >
                <span
                  data-progress={i}
                  className="block h-full w-full rounded-full bg-primary-blue"
                />
              </span>
            ))}
          </div>
        </div>

        {/* the product */}
        <div
          data-stage-fit
          aria-hidden
          className="relative min-h-0 flex-1 lg:h-[min(760px,76vh)] motion-reduce:h-[420px] motion-reduce:flex-none"
        >
          <div
            data-stage
            data-compact="false"
            className="group/stage absolute origin-top-left overflow-hidden rounded-[24px] bg-white text-secondary-dark shadow-[0_50px_140px_-30px_rgba(1,167,255,0.45),0_0_0_1px_rgba(255,255,255,0.12)]"
            style={{ width: STAGE_W, height: STAGE_H }}
          >
            <WindowChrome />
            <div className="absolute inset-x-0 bottom-0 top-12 flex">
              <ModuleRail />
              <div className="relative min-w-0 flex-1">
                <StoreScene />
                <AiScene />
                <FitScene />
                <PmsScene />
                <LabScene />
                <EdgingScene />
                <ShipScene />
              </div>
            </div>
            <div
              data-token
              className="absolute left-0 top-0 z-30 overflow-hidden bg-white shadow-[0_16px_40px_-10px_rgba(1,167,255,0.65)] ring-2 ring-primary-blue motion-reduce:hidden"
              style={{ width: 120, height: 176, visibility: 'hidden' }}
            >
              <TokenFaces />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderTour;
